import { computed, action, observable, reaction } from 'mobx';
import { ViewController } from 'elc-service';
import { emit, events } from 'elc-service-bus';
import * as queryParamUtils from '../utils/QueryParams';

import { ProdcatApiSdk, IItem, ISkuParams, IProductParams } from '../../api/ProdcatApiSdk';
import { ProductGridLoadedUseCase } from '../use-cases/ProductGridLoadedUseCase';
import { PopulateProductsFromServerState } from '../use-cases/PopulateProductsFromServerState';
import { ProductGridSettings } from '../domain/entities/ProductGridSettings';
import { GridItem } from '../domain/entities/GridItem';
import { Product } from '../domain/entities/Product';
import { contentSize, gridItemType } from '../constants/ProductGrid';
import {
    IContentItem,
    IProductGridWrapper,
    IProductGridConfig
} from '../../exported/product-grid-wrapper/ProductGridWrapper';
import { create } from './../domain/services/FilterSetFactory';
import { FilterSet } from '../domain/entities/FilterSet';
import { Filter } from '../domain/entities/Filter';
import { IFilterSets } from '../views/product-filters/IProductFilters';
import { MPPFilteredUseCase } from './../use-cases/MPPFilteredUseCase';
import { ProductGridService } from '../domain/services/ProductGridService';
import { BroadcastProductsLoadedUseCase } from '../use-cases/BroadcastProductsLoadedUseCase';
import { InventoryStatusSortingStrategy } from '../domain/services/InventoryStatusSortingStrategy';
import { mergeConfigs } from '../utils/ConfigUtils';

export enum FILTER_METHODS {
    filter = 'filter',
    sort = 'sort'
}

export interface IProductGridViewControllerData {
    gridItems: GridItem[];
    products: Product[];
    productsNumber: number;
    featuredProductId?: string;
    productSortingDropdownValue: string;
    isFilterDisplayTabbed: boolean;
    isFilterDisplayStacked: boolean;
    isAllGiftsFilterApplied: boolean;
    areFiltersApplied: boolean;
    isLoaded: boolean;
    isPreloaded: boolean;
    categoryTag: string;
    appliedFilters: FilterSet[];
}

export class ProductGridViewController extends ViewController {
    public dataState: IProductGridViewControllerData;
    public configMergeStrategy: Function = mergeConfigs;
    @observable public isPreloaded: boolean = false;
    @observable public isLoaded: boolean = false;
    @observable public positionGridInsideAccordion: boolean = false;

    @observable public productGridService: ProductGridService;
    @observable private productGridSettings: ProductGridSettings;
    @observable private featuredProductId?: string;
    @observable private filtersData: FilterSet[] = [];

    constructor(productGridSettings: ProductGridSettings, productGridService: ProductGridService) {
        super();

        this.productGridSettings = productGridSettings;
        this.productGridService = productGridService;

        reaction(
            () => this.extractedAppliedFilters,
            appliedFilters => {
                if (this.appliedFilters.length) {
                    const mppFiltered = new MPPFilteredUseCase(appliedFilters);
                    mppFiltered.execute();
                }
            }
        );
        reaction(
            () => this.appliedFilters,
            () => {
                this.updateQueryString();
            },
            // this prevents the app from wiping out the query string on initial load
            {
                equals: (previousAppliedFilters, currentAppliedFilters) => {
                    return (
                        previousAppliedFilters.length === 0 && currentAppliedFilters.length === 0
                    );
                }
            }
        );
    }

    @action
    public loaded = async (productGridProps: IProductGridWrapper) => {
        const {
            items: propsItems = [],
            productTags = [],
            content = [],
            paramsById,
            filterData,
            featuredProductId,
            inventoryStatusSorting
        } = productGridProps.config;

        let items: IItem[] = propsItems;
        if (paramsById) {
            items = ProdcatApiSdk.getItemsFromParamsById(paramsById, featuredProductId);
        }
        const itemParams = { items };

        // Load grid cards based on the accessor
        this.featuredProductId = featuredProductId;
        await new ProductGridLoadedUseCase(
            content,
            itemParams,
            productTags,
            this.productGridService
        ).execute();
        this.isLoaded = true;

        // For all the products in the accessor, broadcast they've been loaded
        const {
            product: products
        }: { sku: ISkuParams[]; product: IProductParams[] } = ProdcatApiSdk.splitItemByType(
            this.productGridService.productAccessor
        );
        await new BroadcastProductsLoadedUseCase(products.map(product => product.id)).execute();

        // Load filtersets last
        if (filterData) {
            this.generateFilterSets(filterData.filterSets);
        }

        const isBackorderEnabled =
            productGridProps.config.backorder && productGridProps.config.backorder.enabled;

        if (inventoryStatusSorting && inventoryStatusSorting.enabled) {
            this.productGridSettings.setSortByInventoryStatus(
                inventoryStatusSorting.statuses || [],
                isBackorderEnabled
            );
            const sortingStrategy = new InventoryStatusSortingStrategy(
                inventoryStatusSorting.statuses || [],
                isBackorderEnabled
            );

            this.products.forEach(product => product.sortSkus(sortingStrategy.sortSkusByInventory));
        }
    };

    @computed
    get data(): IProductGridViewControllerData {
        return {
            gridItems: this.gridItems,
            products: this.filteredProducts,
            productsNumber: this.productsNumber,
            featuredProductId: this.featuredProductId,
            productSortingDropdownValue: this.productGridSettings.sortValue,
            isFilterDisplayTabbed: true,
            isFilterDisplayStacked: false,
            isAllGiftsFilterApplied: false,
            areFiltersApplied: this.areFiltersApplied,
            isLoaded: this.isLoaded,
            isPreloaded: this.isPreloaded,
            appliedFilters: this.appliedFilters,
            categoryTag: this.categoryTag
        };
    }

    @computed
    public get categoryTag(): string {
        const defaultCategoryProduct = this.productGridService.products.find((product: Product) =>
            Boolean(product.defaultCategory.value)
        );

        return (defaultCategoryProduct && defaultCategoryProduct.defaultCategory.value) || '';
    }

    @computed public get appliedFilters(): FilterSet[] {
        return this.filtersData.filter(filterSet => filterSet.activeFilters.length);
    }

    @computed public get totalAppliedFilters(): number {
        return this.appliedFilters.reduce((total, filterSet) => {
            return total + filterSet.filters.filter(filter => filter.active).length;
        }, 0);
    }

    @computed public get extractedAppliedFilters(): Filter[] {
        return this.productFilterService.getAppliedFilters(this.appliedFilters);
    }

    @computed public get filters() {
        if (!this.filtersData) {
            return [];
        }

        return this.filtersData;
    }

    @computed
    public get gridItems(): GridItem[] {
        return this.insertContent(this.filteredProducts, this.productGridService.inlineContent);
    }

    @computed
    private get areFiltersApplied() {
        return this.appliedFilters.length > 0;
    }

    // Checks URL for filter params and applies them to the Filter Set
    private applyUrlFilters = (): void => {
        const search = queryParamUtils.getQueryParams();
        const urlParams = new URLSearchParams(search);

        if (search.length > 0 && this.filtersData && this.filtersData.length) {
            // iterate over available filter sets
            this.filtersData.forEach(fSet => {
                const convertedKey = this.productFilterService.convertFilterKeyLabel(fSet.key);
                // compare available filter keys to URL params
                if (urlParams.has(convertedKey)) {
                    // if a key match is found, get the value of the URL param
                    const queryVal = urlParams.get(convertedKey);
                    if (queryVal) {
                        // handle case of multiple comma-delimited values
                        const queryVals: string[] = queryVal.split(',');
                        // find the filter object that matches the URL param key & values
                        queryVals.forEach(val => {
                            const urlFilter = fSet.filters.find(
                                f =>
                                    this.productFilterService.convertFilterValueLabel(f.label) ===
                                    val
                            );
                            if (urlFilter) {
                                // set that filter object to active
                                urlFilter.setActiveState(true);
                            }
                        });
                    }
                }
            });
        }
    };

    // update the Query String to reflect the updated filter state
    private updateQueryString = (): void => {
        const newUrl = this.productFilterService.getAppliedFiltersQueryString(this.appliedFilters);
        window.history.replaceState({}, '', newUrl);
    };

    public insertContent = (productsArray: Product[], content: IContentItem[] = []) => {
        let positionIndex = 0;
        const arrCopy = productsArray.map((product: Product, index) => {
            positionIndex = index + 1;
            if (!this.areFiltersApplied && product.productId === this.featuredProductId) {
                return new GridItem(
                    gridItemType.featuredProduct,
                    this.featuredProductId,
                    positionIndex,
                    {
                        nId: this.featuredProductId,
                        nPsn: 1,
                        nSize: contentSize.large
                    },
                    product
                );
            }

            return new GridItem(
                gridItemType.product,
                product.productId,
                positionIndex,
                {},
                product
            );
        });

        if (arrCopy && arrCopy.length > 0 && content) {
            content.map((contentItem: IContentItem) => {
                positionIndex += 1;
                arrCopy.splice(
                    contentItem.nPsn - 1,
                    0,
                    new GridItem(
                        gridItemType.tout,
                        contentItem.nId.toString(),
                        positionIndex,
                        contentItem
                    )
                );
            });
        }

        return arrCopy;
    };

    public get productFilterService() {
        return this.productGridService.productFilterService;
    }

    public get productSortingService() {
        return this.productGridService.productSortingService;
    }

    @computed
    public get products() {
        return this.productGridService.products;
    }

    @action
    public handleState = (config: IProductGridConfig) => {
        if (!this.dataState) {
            return;
        }
        try {
            const { products, featuredProductId } = this.dataState;
            const { content = [], filterData, items: propsItems = [], paramsById } = config;

            let items: IItem[] = propsItems;
            if (paramsById) {
                items = ProdcatApiSdk.getItemsFromParamsById(paramsById);
            }

            this.productGridService.setProductAccessor(items);
            new PopulateProductsFromServerState(
                products,
                this.productGridService,
                content
            ).execute();

            if (filterData) {
                this.generateFilterSets(filterData.filterSets);
            }

            this.featuredProductId = featuredProductId;
            this.isPreloaded = true;
            this.isLoaded = true;
        } catch (error) {
            console.warn(error);
        }
    };

    @computed
    private get sortedProducts() {
        return this.productSortingService.sortProducts(
            this.productGridService.products,
            this.featuredProductId
        );
    }

    @computed
    public get filteredProducts() {
        if (!this.appliedFilters.length) {
            return this.sortedProducts;
        }

        return this.productFilterService.filter(this.sortedProducts, this.appliedFilters);
    }

    @computed
    public get productsNumber() {
        return this.filteredProducts.length;
    }

    public setProductSortingDropdownValue = async (_: string, value: string) => {
        this.productGridSettings.setSortCriteria(value);
        emit(events.GRID_SORTED, {
            appliedSort: value
        });
    };

    @action
    private generateFilterSets = (filterSetsJson: IFilterSets[]) => {
        const cmsSelectedFilters = create(filterSetsJson);

        this.filtersData = cmsSelectedFilters.reduce(
            (activeFiltersSet: FilterSet[], currentFilterSet: FilterSet): FilterSet[] => {
                const activeFilters = currentFilterSet.filters.reduce(
                    (_activeFilters: Filter[], currentFilter: Filter): Filter[] => {
                        return this.getActiveFiltersForSet(
                            _activeFilters,
                            currentFilter,
                            currentFilterSet
                        );
                    },
                    []
                );

                if (activeFilters.length) {
                    currentFilterSet.setApplicableFilters(activeFilters);
                    activeFiltersSet.push(currentFilterSet);
                }

                return activeFiltersSet;
            },
            []
        );

        // add URL params to Filter Sets
        this.applyUrlFilters();
    };

    private getActiveFiltersForSet(
        _activeFilters: Filter[],
        currentFilter: Filter,
        currentFilterSet: FilterSet
    ): Filter[] {
        if (this.filterAppliesToProducts(currentFilter, currentFilterSet)) {
            _activeFilters.push(currentFilter);
        }

        return _activeFilters;
    }

    private filterAppliesToProducts(currentFilter: Filter, currentFilterSet: FilterSet) {
        return this.productGridService.products.some(product => {
            return currentFilterSet.filterQueries.some(filterSet =>
                currentFilter.passes(filterSet.extract(product))
            );
        });
    }

    @action
    public setGridPosition = () => {
        this.positionGridInsideAccordion = true;
    };
}
